import { useEffect, useMemo, useState, useRef } from 'react'
import { connect } from 'react-redux'
import { bindActionCreators } from 'redux'
import { useTranslation } from 'react-i18next'
import PageModal from '../../common/PageModal'
import SimpleButton from '../../common/SimpleButton'
import ExpandableSection from '../../common/ExpandableSection'
import SimpleTextInput from '../../common/SimpleTextInput'
import CheckboxInput from '../../common/CheckboxInput'
import Dropdown from '../../common/Dropdown'
import { toast } from '../../common/Toast'
import TooltipedIcon from '../../common/TooltipedIcon'
import { InformationIcon, TrashCanFull } from '../../../icons'
import { unitsLanguagesWritePermissions } from '../../../utils/permissionValidation'
import { addUnitsLanguage, updateUnitsLanguage, removeUnitsLanguageTranslation } from '../../../actions/unitsLanguages'
import '../uploads.scss'

const AddEditUnitsLanguagesModal = (props) => {
  const { t } = useTranslation()
  const { langDetails: incomingLangDetails, productTypes, locales = [], closeModal } = props
  const isEditMode = !!incomingLangDetails?.id
  const [productType, setProductType] = useState((incomingLangDetails?.productType || '').toUpperCase())
  const [unitLanguageId, setUnitLanguageId] = useState(incomingLangDetails?.unitLanguageId || '')
  const [isCycleName, setIsCycleName] = useState(!!incomingLangDetails?.isCycleName)
  const [isEventName, setIsEventName] = useState(!!incomingLangDetails?.isEventName)
  const [entries, setEntries] = useState(incomingLangDetails?.unitLanguages || [])
  const [haveError, setHaveError] = useState({})
  const [expandedIdx, setExpandedIdx] = useState(0)
  const [saving, setSaving] = useState(false)
  const entryRefs = useRef({})

  const productTypeOptions = useMemo(() => {
    const names = Array.isArray(productTypes)
      ? productTypes
      : Object.values(productTypes || {})
          .map((p) => p?.name)
          .filter(Boolean)
    const unique = Array.from(new Set(names.map((n) => n.toUpperCase())))
    return unique.sort().map((n) => ({ key: n, value: n }))
  }, [productTypes])

  useEffect(() => {
    if (expandedIdx == null) return
    const node = entryRefs.current[expandedIdx]
    if (node && node.scrollIntoView) {
      node.scrollIntoView({ behavior: 'smooth', block: 'nearest' })
    }
  }, [expandedIdx, entries.length])

  useEffect(() => {
    if (isEditMode) {
      setProductType((incomingLangDetails?.productType || '').toUpperCase())
      setUnitLanguageId(incomingLangDetails?.unitLanguageId || '')
      setIsCycleName(!!incomingLangDetails?.isCycleName)
      setIsEventName(!!incomingLangDetails?.isEventName)
      setEntries(incomingLangDetails?.unitLanguages || [])
      return
    }
    setProductType('')
    setUnitLanguageId('')
    setIsCycleName(false)
    setIsEventName(false)
    setEntries([{ id: null, languageCode: 'EN', locale: 'en-CA', text: '' }])
  }, [isEditMode, incomingLangDetails?.id])
  const languageByLocale = useMemo(() => {
    const matched = {}
    locales.forEach((r) => {
      matched[r.locale] = r.languageCode
    })
    return matched
  }, [locales])

  const allLocaleOptions = useMemo(
    () =>
      Array.from(new Set(locales.map((l) => l.locale))).map((loc) => ({
        key: loc,
        value: loc,
      })),
    [locales],
  )

  const hasAvailableLocales = useMemo(() => {
    if (!allLocaleOptions.length) return false

    const usedLocales = new Set(
      entries
        .filter((e) => !e.isDeleted)
        .map((e) => e.locale)
        .filter(Boolean),
    )

    const allLocales = new Set(allLocaleOptions.map((o) => o.key))

    return usedLocales.size < allLocales.size
  }, [entries, allLocaleOptions])

  const setEntryFields = (idx, patch) => {
    setEntries((prev) => prev.map((e, i) => (i === idx ? { ...e, ...patch } : e)))
  }

  const updateEntry = (idx, patch) => setEntryFields(idx, patch)

  const addEntry = () => {
    setEntries((prev) => {
      const next = [...prev, { id: null, languageCode: '', locale: '', text: '', isDeleted: false }]
      setExpandedIdx(prev.length)
      return next
    })
  }
  const isDefaultEnglish = (entry, idx) => idx === 0 && (entry.languageCode || '').toUpperCase() === 'EN' && entry.locale === 'en-CA'

  const markEntryForDeletion = (idx) => {
    const target = entries[idx]
    if (isDefaultEnglish(target, idx)) return
    setEntries((prev) =>
      prev.map((e, i) =>
        i === idx
          ? {
              ...e,
              isDeleted: true,
            }
          : e,
      ),
    )
  }

  const isLocaleTaken = (loc, idx) => entries.some((e, i) => !e.isDeleted && e.locale === loc && i !== idx)

  const onSelectLocale = (idx, localeKey) => {
    const inferredLang = languageByLocale[localeKey]
    setEntryFields(idx, {
      locale: localeKey,
      ...(inferredLang ? { languageCode: inferredLang } : {}),
    })
  }

  const localeDropdownOptions = (idx) => {
    const unused = []
    const used = []
    allLocaleOptions.forEach((opt) => {
      const loc = opt.key
      if (isLocaleTaken(loc, idx)) used.push(loc)
      else unused.push(loc)
    })

    const ordered = [...unused, ...used]

    return ordered.map((loc) => ({
      key: loc,
      value: loc,
      isDisabled: isLocaleTaken(loc, idx),
    }))
  }

  const duplicateLocaleChosen = (loc, idx) => isLocaleTaken(loc, idx)

  const hasSameInput = () => {
    if (!isEditMode || !incomingLangDetails) return false
    const originalProductType = (incomingLangDetails.productType || '').toUpperCase()
    const currentProductType = (productType || '').toUpperCase()
    const originalUnitLanguageId = incomingLangDetails.unitLanguageId || ''
    const currentUnitLanguageId = unitLanguageId.trim()
    const originalIsCycleName = !!incomingLangDetails.isCycleName
    const originalIsEventName = !!incomingLangDetails.isEventName
    const originalEntries = incomingLangDetails.unitLanguages || []

    return (
      originalProductType === currentProductType &&
      originalUnitLanguageId === currentUnitLanguageId &&
      originalIsCycleName === isCycleName &&
      originalIsEventName === isEventName &&
      JSON.stringify(originalEntries) === JSON.stringify(entries)
    )
  }

  const isValidInput = () => {
    let errors = {}
    let isValid = true

    if (!(productType || '').trim()) {
      errors = { ...errors, productType: true }
      isValid = false
    }

    if (!unitLanguageId.trim()) {
      errors = { ...errors, unitLanguageId: true }
      isValid = false
    }
    const activeEntries = entries.filter((e) => !e.isDeleted)
    if (!activeEntries || activeEntries.length === 0) {
      errors = { ...errors, entries: true }
      isValid = false
    }

    entries.forEach((e, idx) => {
      if (e.isDeleted) return
      const keyPrefix = `entry_${idx}`
      if (!e.languageCode) {
        errors = { ...errors, [`${keyPrefix}_languageCode`]: true }
        isValid = false
      }
      if (!e.locale) {
        errors = { ...errors, [`${keyPrefix}_locale`]: true }
        isValid = false
      }
      if (!e.text || !e.text.trim()) {
        errors = { ...errors, [`${keyPrefix}_text`]: true }
        isValid = false
      }
      if (e.locale && duplicateLocaleChosen(e.locale, idx)) {
        errors = { ...errors, [`${keyPrefix}_locale`]: true }
        isValid = false
      }
    })

    const englishIdx = entries.findIndex((e) => !e.isDeleted && (e.languageCode || '').toUpperCase() === 'EN')
    const english = englishIdx >= 0 ? entries[englishIdx] : null

    if (!english || !english.text || !english.text.trim()) {
      if (englishIdx >= 0) {
        const keyPrefix = `entry_${englishIdx}`
        errors = { ...errors, [`${keyPrefix}_text`]: true }
      }
      errors = { ...errors, englishRequired: true }
      isValid = false
    }
    setHaveError(errors)
    return isValid
  }

  const handleSave = async () => {
    if (!isValidInput()) {
      toast.error(t('error.fill_all_fields'))
      return
    }

    setSaving(true)
    const activeEntries = entries.filter((u) => !u.isDeleted)
    const entriesToDelete = isEditMode ? entries.filter((u) => u.isDeleted && u.id) : []
    const payload = {
      id: incomingLangDetails?.id,
      productType: (productType || '').toUpperCase(),
      unitLanguageId: unitLanguageId.trim(),
      isCycleName,
      isEventName,
      unitLanguages: activeEntries.map((u) => ({
        ...(u.id ? { id: u.id } : {}),
        languageCode: u.languageCode,
        locale: u.locale || '',
        text: u.text || '',
      })),
    }

    try {
      if (isEditMode) {
        if (entriesToDelete.length) {
          await Promise.all(entriesToDelete.map((u) => props.actions.removeUnitsLanguageTranslation(u.id)))
        }
        await props.actions.updateUnitsLanguage(incomingLangDetails.id, payload)
      } else {
        await props.actions.addUnitsLanguage(payload)
      }
      closeModal()
    } finally {
      setSaving(false)
    }
  }

  return (
    <PageModal
      toggle
      onToggle={closeModal}
      className="uploads-modal"
      title={isEditMode ? t('units_languages_context.edit_unit_language') : t('units_languages_context.add_unit_language')}
      footerContent={
        <>
          <SimpleButton className="cancel-button" onClick={closeModal}>
            {t('cancel')}
          </SimpleButton>
          <SimpleButton className="submit-button" onClick={handleSave} disabled={saving || hasSameInput()} requiredPermission={unitsLanguagesWritePermissions}>
            {saving ? t('saving') : t('save')}
          </SimpleButton>
        </>
      }
    >
      <div className="uploads-modal-content">
        <div className="section-title">{t('overview').toUpperCase()}</div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <p className="input-option">{`${t('product_type')}*`}</p>
            {isEditMode ? (
              <div className="value">{productType}</div>
            ) : (
              <Dropdown
                placeholder={t('please_select')}
                options={productTypeOptions}
                initialSelectedKey={productType || undefined}
                onChange={(opt) => setProductType(opt.key)}
                hasError={haveError.productType}
              />
            )}
          </div>
          <div className="input-wrapper">
            <p className="input-option flex-justify-start">
              {`${t('units_languages_context.language_id')}*`}
              <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('units_languages_context.language_id_info')}`} />
            </p>
            {isEditMode ? (
              <div className="value">{unitLanguageId}</div>
            ) : (
              <SimpleTextInput
                value={unitLanguageId}
                onChange={(v) => setUnitLanguageId(v)}
                onClear={() => setUnitLanguageId('')}
                hasError={haveError.unitLanguageId}
              />
            )}
          </div>
        </div>
        <div className="row-wrapper">
          <div className="input-wrapper">
            <CheckboxInput checked={isCycleName} text={t('units_languages_context.is_cycle_name')} onChange={() => setIsCycleName((v) => !v)} />
          </div>
          <div className="input-wrapper">
            <CheckboxInput checked={isEventName} text={t('units_languages_context.is_event_name')} onChange={() => setIsEventName((v) => !v)} />
          </div>
        </div>
        <div className="separator" />
        <div className="section-title">{t('languages').toUpperCase()}</div>
        {hasAvailableLocales && (
          <div className="row-wrapper no-gap d-flex flex-justify-end">
            <button className="add-btn" onClick={addEntry}>
              + {t('units_languages_context.add_translation')}
            </button>
          </div>
        )}
        {entries.map((e, idx) => {
          const keyPrefix = `entry_${idx}`
          const isDeleted = !!e.isDeleted
          return (
            <div
              className="row-wrapper"
              key={(e.id || '') + idx}
              ref={(el) => {
                if (el) entryRefs.current[idx] = el
              }}
              style={isDeleted ? { textDecoration: 'line-through', opacity: 0.6 } : undefined}
            >
              <ExpandableSection
                title={e.locale ? `${e.locale}` : t('users-management.select-language')}
                isExpanded={expandedIdx === idx}
                onToggle={() => setExpandedIdx(expandedIdx === idx ? null : idx)}
                className="with-content-expandable large-body-spacing"
                additionalContent={
                  <div className="expandable-action">
                    {!isDefaultEnglish(e, idx) && !isDeleted && (
                      <TrashCanFull
                        className="action-icon"
                        onClick={(ev) => {
                          ev.stopPropagation()
                          markEntryForDeletion(idx)
                        }}
                      />
                    )}
                  </div>
                }
              >
                <div className="row-wrapper with-sides-no-padding">
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">
                      {`${t('units_languages_context.locale')}*`}
                      <TooltipedIcon icon={<InformationIcon />} tooltipText={`${t('units_languages_context.locale_info')}`} orientation="right" />
                    </p>
                    <Dropdown
                      key={`loc-${idx}-${e.locale || 'none'}-${e.languageCode || 'any'}`}
                      placeholder={t('please_select')}
                      options={localeDropdownOptions(idx)}
                      initialSelectedKey={e.locale || undefined}
                      onChange={(opt) => onSelectLocale(idx, opt.key)}
                      isDisabled={isDeleted || isDefaultEnglish(e, idx)}
                      hasError={!isDeleted && (haveError[`${keyPrefix}_locale`] || haveError[`${keyPrefix}_languageCode`])}
                    />
                  </div>
                  <div className="input-wrapper">
                    <p className="input-option flex-justify-start">{`${t('text')}*`}</p>
                    <SimpleTextInput
                      value={e.text || ''}
                      onChange={(v) => updateEntry(idx, { text: v })}
                      onClear={() => updateEntry(idx, { text: '' })}
                      disabled={isDeleted}
                      hasError={!isDeleted && haveError[`${keyPrefix}_text`]}
                    />
                  </div>
                </div>
              </ExpandableSection>
            </div>
          )
        })}
      </div>
    </PageModal>
  )
}

function stateToProps({ shared, userPermissions }) {
  return {
    languageOptions: shared?.languages?.map((l) => l.languageCode) || [],
    userPermissions,
  }
}

function dispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        addUnitsLanguage,
        updateUnitsLanguage,
        removeUnitsLanguageTranslation,
      },
      dispatch,
    ),
  }
}

export default connect(stateToProps, dispatchToProps)(AddEditUnitsLanguagesModal)

